import React from 'react';
import { SectionInstance } from '../types';
import InlineEditText from '../InlineEditText';
import { sfHeadline } from './sectionHelpers';

interface Platform {
  name?: string;
  icon_url?: string;
  rating?: string;
  review_count?: string;
}

function buildStars(ratingStr: string): string {
  const rating = parseFloat(ratingStr);
  if (isNaN(rating)) return '★★★★★';
  const full = Math.min(5, Math.floor(rating));
  const half = rating - full >= 0.5 ? 1 : 0;
  const empty = 5 - full - half;
  return '★'.repeat(full) + (half ? '½' : '') + '☆'.repeat(empty);
}

const SocialProofBar: React.FC<{ section: SectionInstance }> = ({ section }) => {
  const c = section.controls;
  const bg = (c.background as string) || 'alt';
  const platforms = (c.platforms || []) as Platform[];
  const starRating = (c.star_rating as string) || '4.9';
  const reviewCount = (c.review_count as string) || '500+';

  const bgStyle: React.CSSProperties = { padding: '32px 24px' };
  let textColor = 'inherit';
  let labelColor = '#64748b';
  let dividerColor = 'rgba(0,0,0,0.1)';

  if (bg === 'primary') {
    bgStyle.background = 'var(--brand-primary, #1a365d)';
    bgStyle.color = '#fff';
    textColor = '#fff';
    labelColor = 'rgba(255,255,255,0.75)';
    dividerColor = 'rgba(255,255,255,0.2)';
  } else if (bg === 'alt') {
    bgStyle.background = 'var(--brand-bg-alt, #f8fafc)';
  } else {
    bgStyle.background = '#fff';
  }

  return (
    <section className="sb-render sb-render-social-proof-bar" style={bgStyle}>
      <div style={{
        maxWidth: 1200, margin: '0 auto',
        display: 'flex', alignItems: 'center',
        justifyContent: 'center', gap: 32, flexWrap: 'wrap',
      }}>
        {/* Aggregate rating block */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          <InlineEditText
            tag="p"
            controlKey="headline"
            style={{ fontWeight: 600, fontSize: '1rem', color: textColor, margin: 0 }}
          >
            {sfHeadline(c, 'Trusted by Thousands')}
          </InlineEditText>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: '#f59e0b', fontSize: '1.25rem', letterSpacing: 2 }}>
              {buildStars(starRating)}
            </span>
            <span style={{ fontWeight: 700, fontSize: '1.25rem', color: textColor }}>
              {starRating}
            </span>
          </div>
          <span style={{ fontSize: '0.8rem', color: labelColor }}>
            {reviewCount} reviews
          </span>
        </div>

        {/* Per-platform blocks */}
        {platforms.map((p, i) => (
          <div
            key={i}
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              gap: 4, paddingLeft: 24,
              borderLeft: `1px solid ${dividerColor}`,
            }}
          >
            {p.icon_url ? (
              <img src={p.icon_url} alt={p.name || ''} style={{ height: 20, objectFit: 'contain' }} />
            ) : (
              <span style={{ fontWeight: 700, fontSize: '0.8rem', color: textColor }}>{p.name || ''}</span>
            )}
            {p.rating && (
              <span style={{ fontWeight: 700, fontSize: '0.95rem', color: textColor }}>
                ★ {p.rating}
              </span>
            )}
            {p.review_count && (
              <span style={{ fontSize: '0.75rem', color: labelColor }}>
                {p.review_count} reviews
              </span>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

export default SocialProofBar;
